import { BubbleMenu as BaseBubbleMenu, useEditorState } from "@tiptap/react";
import { TableMap } from "@tiptap/pm/tables";
import deepEql from "fast-deep-equal";
import React, { useCallback, useRef } from "react";

import { PopoverMenu, Icon, SidebarMenu } from "utils/ui";
import type { ColorPickerButtonHandle } from "utils/ui/ColorPickerButton";
import { MenuProps, ShouldShowProps } from "../../../../components/menus/types";
import { findTable, getCellsForSelection, getSelectedColumnIndex, tableHasSpans } from "../../utils";

import { isColumnGripSelected } from "./utils";

export const TableColumnMenu = React.memo(({ editor }: MenuProps): JSX.Element => {
  const colorPickerRef = useRef<ColorPickerButtonHandle>(null);
  const shouldShow = useCallback(
    ({ view, state, from }: ShouldShowProps) => {
      if (!state) {
        return false;
      }

      return isColumnGripSelected({ editor, view, state, from: from || 0 });
    },
    [editor]
  );

  const onAddColumnBefore = useCallback(() => {
    editor.chain().focus().addColumnBefore().run();
  }, [editor]);

  const onAddColumnAfter = useCallback(() => {
    editor.chain().focus().addColumnAfter().run();
  }, [editor]);

  const onMoveColumnLeft = useCallback(() => {
    editor.chain().focus().moveColumn("left").run();
  }, [editor]);

  const onMoveColumnRight = useCallback(() => {
    editor.chain().focus().moveColumn("right").run();
  }, [editor]);

  const onDeleteColumn = useCallback(() => {
    editor.chain().focus().deleteColumn().run();
  }, [editor]);

  const onChangeColor = useCallback(
    (color: string) => {
      editor.chain().focus().setCellsBackgroundColor(color).run();
    },
    [editor]
  );

  const onClearColor = useCallback(() => {
    editor.chain().focus().setCellsBackgroundColor(null).run();
  }, [editor]);

  // editor.state.selection is snapshotted, not reactive on its own — useBlockEditor sets
  // shouldRerenderOnTransaction:false for perf, so this component won't re-render on
  // selection changes unless it independently subscribes via useEditorState (same pattern
  // as useTextmenuStates), otherwise the swatch/move-buttons below go stale after the mount.
  const { currentColor, canMoveLeft, canMoveRight } = useEditorState({
    editor,
    selector: ({ editor }) => {
      const selection = editor.state.selection;
      const currentColor = getCellsForSelection(selection)?.[0]?.node?.attrs?.backgroundColor ?? undefined;

      const table = findTable(selection);
      const columnIndex = getSelectedColumnIndex(selection);
      const canMoveColumns = !!table && columnIndex !== null && !tableHasSpans(table.node);
      const columnCount = table ? TableMap.get(table.node).width : 0;

      return {
        currentColor,
        canMoveLeft: canMoveColumns && (columnIndex as number) > 0,
        canMoveRight: canMoveColumns && (columnIndex as number) < columnCount - 1,
      };
    },
    equalityFn: deepEql,
  });

  return (
    <BaseBubbleMenu
      editor={editor}
      pluginKey="tableColumnMenu"
      updateDelay={0}
      tippyOptions={{
        // Appended to #root (React's actual event-delegation root), not document.body
        // and not the scrolling editor canvas. document.body is body — #root's parent,
        // not a descendant — so content moved there via tippy's raw appendChild sits
        // outside React's delegated listeners and onClick stops firing entirely.
        // #root has no overflow of its own, so it still escapes the canvas's
        // overflow-auto clipping that originally hid this menu behind the sidebar.
        appendTo: () => document.getElementById("root") ?? document.body,
        offset: [0, 15],
        zIndex: 1000,
        popperOptions: {
          modifiers: [{ name: "flip", enabled: false }],
        },
      }}
      shouldShow={shouldShow}
    >
      <div className="w-56 p-1 rounded-lg bg-white dark:bg-black shadow-sm border border-neutral-200 dark:border-neutral-800 text-black dark:text-white">
        <div
          className="flex items-center justify-between px-2 py-1.5 cursor-pointer rounded hover:bg-neutral-100 dark:hover:bg-neutral-900"
          onClick={(e) => colorPickerRef.current?.toggle(e)}
        >
          <span className="flex items-center gap-2 text-sm font-medium text-neutral-500">
            <Icon name="PaintBucket" />
            Background colour
          </span>
          <SidebarMenu.ColorPicker
            ref={colorPickerRef}
            currentColor={currentColor}
            defaultDisplayColor="#ffffff"
            showClearSwatch
            onChangeColor={onChangeColor}
            onClearColor={onClearColor}
            className="h-6 w-6 min-w-6 p-0 flex items-center justify-center hover:opacity-90"
          />
        </div>

        <PopoverMenu.Divider />

        <PopoverMenu.Item
          iconComponent={<Icon name="ArrowLeftToLine" />}
          close={false}
          label="Add column left"
          onClick={onAddColumnBefore}
        />
        <PopoverMenu.Item
          iconComponent={<Icon name="ArrowRightToLine" />}
          close={false}
          label="Add column right"
          onClick={onAddColumnAfter}
        />
        <PopoverMenu.Item
          iconComponent={<Icon name="MoveLeft" />}
          close={false}
          label="Move column left"
          disabled={!canMoveLeft}
          onClick={onMoveColumnLeft}
        />
        <PopoverMenu.Item
          iconComponent={<Icon name="MoveRight" />}
          close={false}
          label="Move column right"
          disabled={!canMoveRight}
          onClick={onMoveColumnRight}
        />

        <PopoverMenu.Divider />

        <PopoverMenu.Item icon="Trash" close={false} label="Delete column" onClick={onDeleteColumn} />
      </div>
    </BaseBubbleMenu>
  );
});

TableColumnMenu.displayName = "TableColumnMenu";

export default TableColumnMenu;
